import React from "react";

import {
    Svg as LabUISvg,
    Props,
    SvgElement,
    svgNames as labuiSvgNames,
} from "@thelabnyc/thelabui/src/components/Svg";

/**
 * thelab-ui provides some icons that might be useful, but you'll
 * definitely need project-specific icons. Put the names of those
 * icons here.
 */
export const svgNames = [
    "caret-right",
    "check",
    "copy",
    "directions",
    "heart",
    "heart-filled",
    "houzz",
    "indeterminate-dash",
    "mail",
    "map-pin",
    "pause",
    "phone",
    "play",
    "search",
    "share",
] as const;

/** If you wish to exclude thelab-ui icons entirely, add their names here. */
const labuiBlacklist = ["arrow", "caret", "download", "envelope"];
const labuiSvgNamesFiltered = labuiSvgNames.filter(
    (name) => !labuiBlacklist.includes(name),
);

/**
 * Project-specific SVGs plus SVGs from thelab-ui.
 *
 * This deduplicates, so if you put something into svgNames that's in
 * labuiSvgNames, it'll only show up once. This is useful if you want
 * to override something from thelab-ui.
 */
export const allSvgNames = [
    ...new Set([...labuiSvgNamesFiltered, ...svgNames].sort()),
] as const;

export type SVGName = (typeof allSvgNames)[number];

export const mapPinD =
    "M21.5 1.92c-10.814 0-19.58 8.803-19.58 19.664a19.54 19.54 0 0 0 5.09 13.224l13.56 15.843a1.222 1.222 0 0 0 1.86 0l13.56-15.843a19.54 19.54 0 0 0 5.09-13.224 19.708 19.708 0 0 0-5.734-13.905A19.538 19.538 0 0 0 21.5 1.919Z";

/**
 * This repo's solution for using icon-like SVGs. "Icon-like" means that the SVG
 * is a relatively small and simple graphic. Most, if not all of our icons are
 * monochromatic, their fills are modified with CSS, and they might be reused in
 * different contexts around the site.
 *
 * This is building around a lab-ui base Svg component, which means that some of
 * these icons are not specific to this repository and might not be what we want
 * to use. They can be overridden if necessary.
 *
 * To add an SVG:
 * - Add a name to `svgNames`
 * - Make the SVG TSX-friendly. https://react-svgr.com/playground/?typescript=true
 *   can help with this
 * - Add a case with your name and your element, replacing `<svg>` with `<SvgElement>`.
 *   You should only need `{...props}` and the `viewBox` attribute; height/width and
 *   fill color can be set with CSS
 *
 * You can hardcode fill colors, but you'll need to be very sure that the color will
 * not change. Typically the logo is a place where you'd do this.
 *
 * Some images might be in the SVG format but not fit this mold, and can be treated as
 * static images instead (placed in the static folder and loaded with `<img>`).
 */
export const Svg = ({ name, ...props }: Props<SVGName>) => {
    switch (name) {
        case "burger":
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M4.32239 6.84087H19.6776C19.8552 6.84087 20 6.652 20 6.42043C20 6.18886 19.8552 6 19.6776 6H4.32239C4.14482 6 4 6.18887 4 6.42043C4 6.65201 4.14482 6.84087 4.32239 6.84087Z" />
                    <path d="M19.6776 11.5798H4.32239C4.14482 11.5798 4 11.7687 4 12.0003C4 12.2318 4.14482 12.4207 4.32239 12.4207H19.6776C19.8552 12.4207 20 12.2318 20 12.0003C20 11.7687 19.8552 11.5798 19.6776 11.5798Z" />
                    <path d="M19.6776 17.1592H4.32239C4.14482 17.1592 4 17.348 4 17.5796C4 17.8112 4.14482 18 4.32239 18H19.6776C19.8552 18 20 17.8112 20 17.5796C20 17.348 19.8552 17.1592 19.6776 17.1592Z" />
                </SvgElement>
            );
        case "caret-right":
            return (
                <SvgElement {...props} viewBox="0 0 40 40">
                    <path d="m13.488 6.645-.199.199-.197.2-.002-.001-.703.709 12.107 12L12.498 31.75l.2.197.198.2.311.31.197.197.2.2 13.105-13.106z" />
                </SvgElement>
            );
        case "check":
            return (
                <SvgElement {...props} viewBox="0 0 13 10">
                    <path d="M11.293.293 4.5 7.086 2.207 4.793.793 6.207 4.5 9.914l8.207-8.207Z" />
                </SvgElement>
            );
        case "copy":
            return (
                <SvgElement {...props} viewBox="0 0 20 20">
                    <path
                        fillRule="evenodd"
                        d="M4 2a2 2 0 00-2 2v9a2 2 0 002 2h2v2a2 2 0 002 2h9a2 2 0 002-2V8a2 2 0 00-2-2h-2V4a2 2 0 00-2-2H4zm9 4V4H4v9h2V8a2 2 0 012-2h5zM8 8h9v9H8V8z"
                    />
                </SvgElement>
            );
        case "directions":
            return (
                <SvgElement {...props} viewBox="0 0 29 29 ">
                    <path
                        fillRule="evenodd"
                        d="M28.627 13.927 15.93 1.231a1.405 1.405 0 0 0-1.989 0L1.245 13.927c-.55.55-.55 1.439 0 1.99L13.94 28.612c.55.55 1.439.55 1.99 0l12.696-12.697c.55-.536.55-1.425 0-1.989zM17.75 18.455V14.93h-5.643v4.232H9.286v-5.643c0-.776.635-1.41 1.41-1.41h7.054V8.58l4.938 4.938z"
                    />
                </SvgElement>
            );
        case "heart":
            return (
                <SvgElement {...props} viewBox="0 0 16 16">
                    <path d="M11.578 1.8c-.582.013-1.188.174-1.811.501l-.008.004h-.002l-.006.004c-.31.164-.602.364-.94.598-.174.118-.359.245-.564.379l-.34.222-.263-.263-.314-.235c-.22-.17-.417-.32-.63-.45-1.314-.807-2.745-.847-3.904-.324C1.637 2.76.75 3.846.536 5.346c-.214 1.514.536 2.747 1.62 3.884 1.62 1.7 3.322 3.332 5.034 4.962.254.243.443.408.586.502.142.095.206.106.24.106.031 0 .088-.006.223-.096v-.002c.138-.09.323-.247.57-.484 1.708-1.632 3.395-3.278 5.018-4.978.523-.548.96-1.245 1.297-1.96.72-1.532.384-3.126-.818-4.297-.672-.658-1.402-1.03-2.156-1.145a3.337 3.326 0 0 0-.572-.037zm-.138 1.513c.073-.003.147-.003.22.002.593.033 1.12.303 1.569.732.458.436.774.945.81 1.543.034.592-.206 1.183-.637 1.796-1.394 1.983-3.191 3.55-5.282 5.152l-.353.271-.31-.32c-.484-.502-.97-.993-1.455-1.483-1.124-1.134-2.234-2.255-3.259-3.45l-.007-.007-.006-.008c-.507-.592-.79-1.18-.79-1.78.002-.603.293-1.126.708-1.578l.002-.002c.457-.497 1.013-.814 1.648-.86.63-.045 1.272.17 1.933.581.45.28.87.61 1.332.97.132.105.274.215.417.325l.418-.313c.493-.372.965-.727 1.466-1.031l.018-.012.01-.004c.526-.316 1.04-.504 1.548-.524z" />
                </SvgElement>
            );
        case "heart-filled":
            return (
                <SvgElement {...props} viewBox="0 0 16 16">
                    <path d="M11.578 1.8c-.582.013-1.188.174-1.811.501l-.008.004h-.002l-.006.004a9.55 9.55 0 0 0-.94.598c-.174.118-.359.245-.564.379l-.34.222-.262-.263-.315-.235c-.22-.17-.417-.32-.63-.45-1.314-.807-2.745-.847-3.904-.324C1.637 2.76.75 3.846.536 5.346c-.214 1.514.536 2.747 1.62 3.884 1.62 1.7 3.322 3.332 5.034 4.962.254.243.444.408.586.502.142.095.206.106.24.106.031 0 .088-.006.223-.096v-.002c.138-.09.323-.247.57-.484 1.708-1.632 3.395-3.278 5.018-4.978.523-.548.96-1.245 1.297-1.96.72-1.532.384-3.126-.818-4.297-.672-.658-1.402-1.03-2.156-1.145a3.337 3.326 0 0 0-.572-.037z" />
                </SvgElement>
            );
        case "houzz":
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m 13.944444,21.259258 v 0 H 19.5 v 0 -10.648144 0 L 8.3888892,7.0925944 v 0 -4.3518524 0 H 4.5 v 0 18.518516 0 h 5.555556 v 0 -6.203704 0 h 3.888888 v 0 6.203704 0"></path>
                </SvgElement>
            );
        case "indeterminate-dash":
            return (
                <SvgElement {...props} viewBox="0 0 13 10">
                    <rect width="10" height="2" x="1.5" y="4" />
                </SvgElement>
            );
        case "mail":
            return (
                <SvgElement {...props} viewBox="0 0 6.879 6.879">
                    <path d="m.78 1.469-.35.396L3.266 4.36a.265.265 0 0 0 .35-.002l2.821-2.492-.35-.396L3.44 3.807Zm.075-.067a.52.52 0 0 0-.515.516v3.258a.52.52 0 0 0 .515.515H6.01a.522.522 0 0 0 .517-.515V1.918a.522.522 0 0 0-.517-.516zm.014.53h5.129v3.23H.869Z" />
                </SvgElement>
            );
        case "map-pin":
            return (
                <SvgElement {...props} viewBox="0 0 43 53">
                    <path fillRule="evenodd" d={mapPinD} />
                </SvgElement>
            );
        case "pause":
            return (
                <SvgElement {...props} viewBox="0 0 42 42">
                    <path d="M16.528 12.47a.51.51 0 0 0-.5.52v15.615c0 .287.224.52.5.52a.51.51 0 0 0 .5-.52V12.99a.511.511 0 0 0-.5-.52ZM24.67 12.99v15.614c0 .287.224.52.5.52a.51.51 0 0 0 .5-.52V12.99a.51.51 0 0 0-.5-.52c-.276 0-.5.233-.5.52Z" />
                    <path
                        fillRule="evenodd"
                        d="M2 21c0 10.493 8.507 19 19 19s19-8.507 19-19S31.493 2 21 2 2 10.507 2 21ZM21 1C9.954 1 1 9.954 1 21s8.954 20 20 20 20-8.954 20-20S32.046 1 21 1Z"
                        clipRule="evenodd"
                    />
                    <path
                        fillRule="evenodd"
                        d="M.5 21C.5 9.678 9.678.5 21 .5S41.5 9.678 41.5 21 32.322 41.5 21 41.5.5 32.322.5 21ZM21 2.5C10.783 2.5 2.5 10.783 2.5 21S10.783 39.5 21 39.5 39.5 31.217 39.5 21 31.217 2.5 21 2.5Zm4.169 9.47h.001c.571 0 1 .476 1 1.02v15.614c0 .544-.43 1.02-1 1.02-.572 0-1-.476-1-1.02V12.99c0-.545.43-1.019.999-1.02Zm-9.641 1.02c0-.544.429-1.02 1-1.02.57 0 .998.476 1 1.019v15.616c0 .544-.43 1.02-1 1.02-.571 0-1-.476-1-1.02V12.99Z"
                        clipRule="evenodd"
                    />
                </SvgElement>
            );
        case "phone":
            return (
                <SvgElement {...props} viewBox="0 0 26 26">
                    <path
                        d="M5.339 11.222a21.37 21.37 0 0 0 9.297 9.296l3.103-3.103c.381-.381.945-.508 1.439-.339 1.58.522 3.287.804 5.036.804.776 0 1.411.635 1.411 1.41v4.924c0 .776-.635 1.411-1.41 1.411C10.967 25.625.231 14.889.231 1.643c0-.776.635-1.41 1.41-1.41H6.58c.776 0 1.411.634 1.411 1.41 0 1.763.282 3.456.804 5.036a1.416 1.416 0 0 1-.353 1.439z"
                        fillRule="evenodd"
                    />
                </SvgElement>
            );
        case "play":
            return (
                <SvgElement {...props} viewBox="0 0 40 40">
                    <path d="M20 0C8.967 0 0 8.967 0 20s8.967 20 20 20 20-8.967 20-20S31.033 0 20 0Zm0 2c9.953 0 18 8.047 18 18s-8.047 18-18 18S2 29.953 2 20 10.047 2 20 2Zm-5.5 9v18l15-9-15-9Z" />
                </SvgElement>
            );
        case "search":
            return (
                <SvgElement {...props} viewBox="0 0 16 16">
                    <path d="M14.922 14.655a.938.938 0 0 0-.277-.669l-3.983-3.983.146-.246c.493-.83.754-1.781.754-2.75a5.412 5.412 0 0 0-5.406-5.406A5.412 5.412 0 0 0 .75 7.007a5.412 5.412 0 0 0 5.406 5.405 5.38 5.38 0 0 0 2.949-.877l.25-.163 3.953 3.952a.94.94 0 0 0 .669.277c.252 0 .49-.098.668-.277a.939.939 0 0 0 .277-.669zm-8.766-3.624a4.028 4.028 0 0 1-4.023-4.023 4.028 4.028 0 0 1 4.023-4.023 4.028 4.028 0 0 1 4.023 4.023 4.028 4.028 0 0 1-4.023 4.023Z" />
                </SvgElement>
            );
        case "share":
            return (
                <SvgElement {...props} viewBox="0 0 21 20">
                    <path d="M13.6965 18C13.002 18 12.4117 17.7569 11.9256 17.2708C11.4395 16.7847 11.1965 16.1944 11.1965 15.5C11.1965 15.375 11.2242 15.1736 11.2798 14.8958L6.34231 11.875C6.12008 12.0694 5.86974 12.2222 5.59126 12.3333C5.31293 12.4444 5.01467 12.5 4.69647 12.5C4.00203 12.5 3.41175 12.2569 2.92564 11.7708C2.43953 11.2847 2.19647 10.6944 2.19647 10C2.19647 9.30556 2.43953 8.71528 2.92564 8.22917C3.41175 7.74306 4.00203 7.5 4.69647 7.5C5.01592 7.5 5.31453 7.55556 5.59231 7.66667C5.87008 7.77778 6.12008 7.93056 6.34231 8.125L11.2798 5.10417C11.252 5.00694 11.2312 4.91104 11.2173 4.81646C11.2034 4.72188 11.1965 4.61639 11.1965 4.5C11.1965 3.80556 11.4395 3.21528 11.9256 2.72917C12.4117 2.24306 13.002 2 13.6965 2C14.3909 2 14.9812 2.24306 15.4673 2.72917C15.9534 3.21528 16.1965 3.80556 16.1965 4.5C16.1965 5.19444 15.9534 5.78472 15.4673 6.27083C14.9812 6.75694 14.3909 7 13.6965 7C13.377 7 13.0784 6.94444 12.8006 6.83333C12.5229 6.72222 12.2729 6.56944 12.0506 6.375L7.11314 9.39583C7.14092 9.49306 7.16175 9.58896 7.17564 9.68354C7.18953 9.77813 7.19647 9.88361 7.19647 10C7.19647 10.1164 7.18953 10.2219 7.17564 10.3165C7.16175 10.411 7.14092 10.5069 7.11314 10.6042L12.0506 13.625C12.2729 13.4306 12.5232 13.2778 12.8017 13.1667C13.08 13.0556 13.3783 13 13.6965 13C14.3909 13 14.9812 13.2431 15.4673 13.7292C15.9534 14.2153 16.1965 14.8056 16.1965 15.5C16.1965 16.1944 15.9534 16.7847 15.4673 17.2708C14.9812 17.7569 14.3909 18 13.6965 18ZM13.6965 16.5C13.9798 16.5 14.2173 16.4042 14.409 16.2125C14.6006 16.0208 14.6965 15.7833 14.6965 15.5C14.6965 15.2167 14.6006 14.9792 14.409 14.7875C14.2173 14.5958 13.9798 14.5 13.6965 14.5C13.4131 14.5 13.1756 14.5958 12.984 14.7875C12.7923 14.9792 12.6965 15.2167 12.6965 15.5C12.6965 15.7833 12.7923 16.0208 12.984 16.2125C13.1756 16.4042 13.4131 16.5 13.6965 16.5ZM4.69647 11C4.97981 11 5.21731 10.9042 5.40897 10.7125C5.60064 10.5208 5.69647 10.2833 5.69647 10C5.69647 9.71667 5.60064 9.47917 5.40897 9.2875C5.21731 9.09583 4.97981 9 4.69647 9C4.41314 9 4.17564 9.09583 3.98397 9.2875C3.79231 9.47917 3.69647 9.71667 3.69647 10C3.69647 10.2833 3.79231 10.5208 3.98397 10.7125C4.17564 10.9042 4.41314 11 4.69647 11ZM13.6965 5.5C13.9798 5.5 14.2173 5.40417 14.409 5.2125C14.6006 5.02083 14.6965 4.78333 14.6965 4.5C14.6965 4.21667 14.6006 3.97917 14.409 3.7875C14.2173 3.59583 13.9798 3.5 13.6965 3.5C13.4131 3.5 13.1756 3.59583 12.984 3.7875C12.7923 3.97917 12.6965 4.21667 12.6965 4.5C12.6965 4.78333 12.7923 5.02083 12.984 5.2125C13.1756 5.40417 13.4131 5.5 13.6965 5.5Z" />
                </SvgElement>
            );
        case "x":
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M18.6525 4.52973L11.9734 11.1517L5.40843 4.53019C5.16877 4.29053 4.77153 4.29007 4.53186 4.52974C4.2922 4.7694 4.2922 5.16619 4.53186 5.40585L11.1255 11.9995L4.47198 18.5936C4.23231 18.8333 4.23184 19.2306 4.4715 19.4702C4.71112 19.7099 5.10779 19.7099 5.34747 19.4704L12.0267 12.8482L18.5916 19.4698C18.8313 19.7094 19.2285 19.7099 19.4682 19.4702C19.7079 19.2306 19.7079 18.8338 19.4682 18.5941L12.8746 12.0005L19.5281 5.40632C19.7678 5.16666 19.7682 4.7694 19.5286 4.52973C19.289 4.29014 18.8921 4.29029 18.6525 4.52973Z" />
                </SvgElement>
            );
        default:
            return <LabUISvg name={name} {...props}></LabUISvg>;
    }
};
